/* eslint-disable @typescript-eslint/camelcase */
import {
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
} from '@material-ui/core';
import { Cancel, Delete } from '@material-ui/icons';
import { SearchBar } from 'components/SearchBar';
import TableItems, {
  StatusIcon,
  TableCell,
  TableRow,
} from 'components/TableItems';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import Permission from 'components/ValidPermission';
import { useCheckboxes } from 'hooks';
import { useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { PermissionsData } from 'modules/authenticate/interfaces';
import {
  getEnvironmentSessionsRequest,
  removeSessionsInstanceRequest,
} from 'modules/environments/actions';
import {
  EnvironmentFields,
  ListEnvironmentSessionsProps,
  SessionsInInstance,
} from 'modules/environments/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { formatDateWithMoment } from 'utils/helpers';
import RefreshButton from 'components/RefreshButton';
import Button from 'components/Button';
import FormRemoveSession from '../ListInstances/InstanceSessions/FormRemoveSession';
import EnvironmentsTemplate from '../Templates/Environments';
import styles from './styles';

const mapStatus = (status: string) => {
  const map = {
    init: <CircularProgress size={16} color="secondary" />,
    removing: <CircularProgress size={16} color="secondary" />,
    active: <StatusIcon status="success" />,
    close: <StatusIcon status="blocked" />,
  };

  return map[status] || status;
};

interface SessionsProps {
  environmentSelected: EnvironmentFields | null;
  environmentSessionsList: ListEnvironmentSessionsProps;
  isLoading: boolean;
  permissions?: PermissionsData;
  getEnvironmentSessionsRequest: ({ environmentUuid, query }) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  removeSessionsInstanceRequest: ({ values, getSessions }) => void;
}
const Sessions = ({
  environmentSelected,
  environmentSessionsList,
  isLoading,
  permissions,
  getEnvironmentSessionsRequest,
  modalClose,
  modalOpen,
  removeSessionsInstanceRequest,
}: SessionsProps) => {
  const classes = makeStyles(styles)();
  const { id } = useParams<{ id: string }>();
  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters();
  const {
    checks,
    handleCheck,
    handleAllCheck,
    isChecked,
    clearChecks,
  } = useCheckboxes<SessionsInInstance, string>({
    items: environmentSessionsList?.results?.filter(
      item => !['close', 'removing'].includes(item?.status)
    ),
    isItemChecked: (item, check) => item.session_id === check,
    createCheckItem: item => item.session_id,
  });
  const isAllChecked =
    !!environmentSessionsList?.results?.length &&
    environmentSessionsList.results.every(item => isChecked(item?.session_id));

  const [refresh, setRefresh] = React.useState(false);

  const headers: Array<TableCellHeaderProps & { hidden?: boolean }> = [
    {
      key: 'name',
      i18n: 'NAME_KEY',
      order: true,
      centerTitle: false,
    },
    {
      key: 'email',
      i18n: 'EMAIL_KEY',
      order: true,
      centerTitle: false,
    },
    {
      key: 'instance',
      i18n: 'INSTANCE_KEY',
      order: true,
      centerTitle: false,
    },
    {
      key: 'creation_date',
      i18n: 'CREATION_DATE_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'status',
      i18n: 'STATUS_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'actions',
      i18n: 'ACTIONS_KEY',
      order: false,
      centerTitle: true,
      hidden: !permissions?.delete_environment_instance_sessions,
    },
  ].filter(item => !item.hidden);

  const getEnvironmentSessions = React.useCallback(
    (refresh = false) => {
      getEnvironmentSessionsRequest({ environmentUuid: id, query });
      setRefresh(refresh);
    },
    [getEnvironmentSessionsRequest, id, query]
  );

  React.useEffect(() => {
    getEnvironmentSessions();
  }, [getEnvironmentSessions]);

  const handleRemoveSessions = (sessions: any[], type: string) => {
    let title = i18n.t('CLIENTS_PAGE.REMOVE_MULTIPLE_SESSIONS_TITLE_KEY');
    let subtitle = i18n.t(
      'CLIENTS_PAGE.REMOVE_MULTIPLE_SESSIONS_DESCRIPTION_KEY'
    );

    if (type !== 'multiple') {
      title = i18n.t('REMOVE_SESSION_KEY');
      subtitle = i18n.t('REMOVE_SESSION_DESCRIPTION_KEY', {
        name: `${sessions[0]?.user?.first_name} ${sessions[0]?.user?.last_name}`,
      });
    }

    modalOpen({
      title,
      subtitle,
      content: (
        <FormRemoveSession
          onCancel={modalClose}
          onSubmit={(values: any) => {
            removeSessionsInstanceRequest({
              values: {
                ...values,
                sessions_ids: sessions.map(({ session_id }) => session_id),
              },
              getSessions: getEnvironmentSessions,
            });

            if (type === 'multiple') {
              clearChecks();
            }
            modalClose();
          }}
        />
      ),
      maxWidth: 'sm',
    });
  };

  return (
    <EnvironmentsTemplate
      currentPage="sessions"
      titlePage={environmentSelected?.name}
      breadcrumbs={[
        {
          name: i18n.t('ENVIRONMENTS_KEY'),
          link: '/environments',
        },
        {
          name: i18n.t('SESSIONS_KEY'),
        },
      ]}
      titleContent={`${i18n.t('ENVIRONMENTS_PAGE.LIST_SESSIONS_TITLE_KEY')}`}
      subtitleContent={i18n.t('ENVIRONMENTS_PAGE.LIST_SESSIONS_SUBTITLE_KEY')}
      actionsContent={
        <Grid container alignItems="center" justify="flex-end" xs={6}>
          <RefreshButton
            id="btn-refresh"
            animate={isLoading}
            onClick={() => getEnvironmentSessions(true)}
            disabled={isLoading}
          />
          <Button
            id="remove-session-bulk-client-button"
            variant="contained"
            className={classes.removeButton}
            startIcon={<Delete />}
            onClick={() =>
              handleRemoveSessions(
                checks.map(item => ({ session_id: item })),
                'multiple'
              )
            }
            disabled={checks.length === 0}
          >
            {i18n.t('REMOVE_SESSIONS_KEY')}
          </Button>
        </Grid>
      }
    >
      <Grid container>
        <Grid item xs={6}>
          <SearchBar
            id="search-sessions"
            isLoading={false}
            searchValue={searchValue}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
        </Grid>

        <TableItems
          header={
            <>
              <Permission value="delete_environment_instance_sessions">
                <TableCell className={classes.checkboxCell}>
                  <Checkbox
                    id="select-all-sessions"
                    checked={isAllChecked}
                    onClick={() => handleAllCheck(isAllChecked)}
                  />
                </TableCell>
              </Permission>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  order={item?.order}
                  centerTitle={item?.centerTitle}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                  markOderItem={itemOrder === item?.key}
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: SessionsInInstance) => (
            <TableRow key={item?.session_id}>
              <Permission value="delete_environment_instance_sessions">
                <TableCell className={classes.checkboxCell}>
                  <Checkbox
                    id="select-session"
                    checked={!!checks.find(check => check === item?.session_id)}
                    onChange={() => handleCheck(item?.session_id)}
                    disabled={['close', 'removing'].includes(item?.status)}
                  />
                </TableCell>
              </Permission>
              <TableCell>
                {item?.user.first_name} {item?.user.last_name}
              </TableCell>
              <TableCell>{item?.user.email}</TableCell>
              <TableCell ellipsis maxLength={20} tooltip={item?.instance_id}>
                {item?.instance_id ? (
                  <Permission
                    value="list_environment_instances"
                    fallback={item?.instance_id}
                  >
                    <Link
                      className={classes.linkToInstances}
                      to={`/environments/${item?.environment_uuid}/instances?id=${item?.instance_id}`}
                    >
                      {item?.instance_id}
                    </Link>
                  </Permission>
                ) : (
                  <Tooltip
                    title={i18n.t('CLIENTS_PAGE.USER_NO_LINKED_INSTANCE_KEY')}
                    placement="top"
                  >
                    <Cancel className={classes.iconCancel} />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell className={classes.dateCell}>
                {formatDateWithMoment(item?.login_at)}
              </TableCell>
              <TableCell
                centerTitle
                tooltip={i18n.t(
                  item?.status === 'close'
                    ? i18n.t('CLOSED_KEY')
                    : `${item?.status.toUpperCase()}_KEY`
                )}
              >
                <Grid container justify="center">
                  {mapStatus(item?.status)}
                </Grid>
              </TableCell>
              <Permission value="delete_environment_instance_sessions">
                <TableCell textAlignCenter>
                  <IconButton
                    className={classes.removeAction}
                    onClick={() => handleRemoveSessions([item], 'single')}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </Permission>
            </TableRow>
          )}
          data={environmentSessionsList}
          isLoading={isLoading && !refresh}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('CLIENTS_PAGE.NO_SESSIONS_KEY')}
        />
      </Grid>
    </EnvironmentsTemplate>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: {
    environmentSelected,
    environmentSessionsList,
    isLoadingEnvironmentSessionsList,
  },
}: ApplicationState) => ({
  environmentSelected,
  environmentSessionsList,
  isLoading: isLoadingEnvironmentSessionsList,
  permissions: data?.permissions,
});

const mapDispatchToProps = {
  getEnvironmentSessionsRequest,
  modalClose,
  modalOpen,
  removeSessionsInstanceRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sessions);
